import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, LifeBuoy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { SupportSession, SupportSessionStatus } from '../../common/hooks/use-support-sessions-api.hook';
// Leaf imports: the admin index pulls in the whole app shell.
import { EmptyState } from '../../components/design-system/admin/EmptyState';
import { RelativeTime } from '../../components/design-system/admin/RelativeTime';
import { SectionCard } from '../../components/design-system/admin/SectionCard';
import { StatusBadge, StatusTone } from '../../components/design-system/admin/StatusBadge';
import { describeAction, wentThrough } from './describe-action';

const STATUS_TONE: Record<SupportSessionStatus, StatusTone> = { active: 'warning', ended: 'neutral', expired: 'neutral' };

export interface SupportSessionsHistoryProps {
    load: () => Promise<SupportSession[]>;
    /** 'admin' can resume or end a live session; 'vendor' only reads. */
    audience: 'admin' | 'vendor';
    /** Admin only: the super admin signed in, to offer Resume on their own live session. */
    currentEmail?: string;
    onResume?: (session: SupportSession) => void;
    onEnd?: (session: SupportSession) => Promise<void>;
}

/** Every support session in one vendor's account: who, when, why, how, and what changed. */
export function SupportSessionsHistory({ load, audience, currentEmail, onResume, onEnd }: SupportSessionsHistoryProps) {
    const { t } = useTranslation();
    const [sessions, setSessions] = useState<SupportSession[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [open, setOpen] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const refresh = async () => {
        try {
            setSessions(await load());
            setFailed(false);
        } catch (error) {
            console.error('Error loading support sessions:', error);
            setFailed(true);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const end = async (s: SupportSession) => {
        if (!onEnd) return;
        setBusy(s._id);
        try {
            await onEnd(s);
            await refresh();
        } finally {
            setBusy(null);
        }
    };

    return (
        <SectionCard
            title={t('SUPPORT_ACCESS.HISTORY_TITLE')}
            description={t(audience === 'admin' ? 'SUPPORT_ACCESS.HISTORY_DESCRIPTION_ADMIN' : 'SUPPORT_ACCESS.HISTORY_DESCRIPTION')}
            flush
            testId="support-sessions"
        >
            {failed ? (
                <div className="flex items-center justify-between gap-3 border-t px-6 py-4">
                    <p className="text-sm text-muted-foreground">{t('SUPPORT_ACCESS.LOAD_FAILED')}</p>
                    <Button variant="outline" size="sm" onClick={refresh}>{t('ADMIN_SHELL.COMMON.RETRY')}</Button>
                </div>
            ) : !sessions ? (
                <div className="px-6 pb-4"><Skeleton className="h-16 rounded-xl" /></div>
            ) : sessions.length === 0 ? (
                <div className="border-t">
                    <EmptyState icon={<LifeBuoy />} title={t('SUPPORT_ACCESS.HISTORY_EMPTY')} />
                </div>
            ) : (
                <ul className="divide-y border-t">
                    {sessions.map((s) => {
                        const expanded = open === s._id;
                        // The vendor sees what changed; support also sees what was refused, and how it was called.
                        const shown = audience === 'vendor' ? s.actions.filter(wentThrough) : s.actions;
                        const changes = s.changeCount ?? s.actionCount;
                        const mine = audience === 'admin' && s.status === 'active' && !!currentEmail && s.startedBy === currentEmail;
                        return (
                            <li key={s._id} className="flex flex-col gap-2 px-6 py-3" data-testid="support-session" data-status={s.status}>
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex min-w-0 flex-col gap-0.5">
                                        <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                                            <span className="truncate">{s.startedBy}</span>
                                            <StatusBadge tone={s.mode === 'edit' ? 'info' : 'neutral'}>
                                                {t(s.mode === 'edit' ? 'SUPPORT_ACCESS.MODE_EDIT' : 'SUPPORT_ACCESS.MODE_VIEW')}
                                            </StatusBadge>
                                            <StatusBadge tone={STATUS_TONE[s.status]}>{t(`SUPPORT_ACCESS.STATUS.${s.status.toUpperCase()}`)}</StatusBadge>
                                        </div>
                                        <span className="text-xs text-muted-foreground">
                                            <RelativeTime value={s.startedAt} />
                                            {' · '}
                                            {changes > 0 ? t('SUPPORT_ACCESS.CHANGES', { count: changes }) : t('SUPPORT_ACCESS.NO_CHANGES')}
                                        </span>
                                        {s.reason && (
                                            <span className="text-xs text-muted-foreground" data-testid="support-session-reason">
                                                {t('SUPPORT_ACCESS.REASON')}: {s.reason}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                        {shown.length > 0 && (
                                            <Button variant="ghost" size="sm" onClick={() => setOpen(expanded ? null : s._id)} aria-expanded={expanded}>
                                                {expanded ? <ChevronUp /> : <ChevronDown />}
                                                {t(expanded ? 'SUPPORT_ACCESS.HIDE_CHANGES' : 'SUPPORT_ACCESS.SHOW_CHANGES')}
                                            </Button>
                                        )}
                                        {mine && onResume && (
                                            <Button variant="outline" size="sm" onClick={() => onResume(s)}>{t('SUPPORT_ACCESS.RESUME')}</Button>
                                        )}
                                        {audience === 'admin' && s.status === 'active' && onEnd && (
                                            <Button variant="outline" size="sm" onClick={() => end(s)} disabled={busy === s._id}>
                                                {t('SUPPORT_ACCESS.END_NOW')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                {expanded && (
                                    <ul className="flex flex-col divide-y rounded-lg border" data-testid="support-session-actions">
                                        {shown.map((a, i) => {
                                            const { sentence, details } = describeAction(a, t);
                                            const failed = !wentThrough(a);
                                            return (
                                                <li key={i} className="flex items-start justify-between gap-4 px-3 py-2" data-testid="support-session-action">
                                                    <div className="flex min-w-0 flex-col gap-0.5">
                                                        <span className={failed ? 'text-sm text-muted-foreground line-through' : 'text-sm'}>{sentence}</span>
                                                        {details && <span className="text-xs text-muted-foreground">{details}</span>}
                                                        {audience === 'admin' && (
                                                            <span className="break-all font-mono text-[11px] text-muted-foreground" data-testid="support-session-action-technical">
                                                                {a.method} {a.path}
                                                                {a.status !== undefined && (
                                                                    <span className={failed ? 'text-destructive' : undefined}> · {a.status}</span>
                                                                )}
                                                                {failed && <> · {t('SUPPORT_ACCESS.CHANGE.FAILED')}</>}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="shrink-0 text-xs text-muted-foreground"><RelativeTime value={a.at} /></span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </SectionCard>
    );
}
